import type { ElationClinical } from "../bridge/types";

/**
 * T6 (QA sheet): "Remove BP Recordings (unless it is connected to the EHR.
 * Encounter notes will need to be connected to the EHR and transferred over)."
 *
 * The "BP Recordings" section inside `@astrana/ui`'s ClinicalSummary card is
 * gated on the EHR-chart condition ALONE: a chart open in the CoPilot sidecar
 * with a visit note open in it.
 *
 * ## Deliberately NOT persona-gated
 *
 * BP Recordings is a SECTION INSIDE the Patient Summary / ClinicalSummary
 * module — it is not a module of its own. An earlier revision ANDed a
 * `canSeeModule("BpRecordings")` grant onto this predicate, which invented a
 * dependency on a `member_360.persona_modules` row that exists in NO
 * environment: under fail-closed enforcement that would have hidden BP even in
 * the sidecar with a chart open, and un-hiding it would have needed SQL in
 * every env. The chart condition alone satisfies the requirement, with no SQL.
 * Whoever can see the ClinicalSummary card can see its BP section.
 *
 * ## Why `clinical.openNote`, and not `status`/`sourceTabActive`
 *
 * `useElationBridge()` returns three candidate signals. Only one of them means
 * what the requirement says:
 *
 *  - `status === "connected"` — an Elation TAB with a chart is reachable. That
 *    satisfies "connected to the EHR" but NOT the second half of the sentence
 *    ("encounter notes will need to be connected"): it is true while the
 *    provider is merely parked on a chart with no note started, which is
 *    exactly the case the requirement wants BP hidden for.
 *  - `sourceTabActive` — the scraped Elation tab is the FOREGROUND tab. This is
 *    a transient window-focus fact, not a clinical one; gating on it would make
 *    the BP section appear and disappear as the provider alt-tabs, and it says
 *    nothing about a note. Wrong signal.
 *  - `clinical.openNote` — a visit note is OPEN in the chart right now. This is
 *    the exact conjunction the requirement asks for: `openNote` is only ever
 *    populated from an `ELATION_DATA` payload produced by a scrape of a live
 *    chart, so a non-null `openNote` STRICTLY IMPLIES `status === "connected"`.
 *    Requiring `status` on top would be redundant, not safer.
 *
 * This mirrors `MemberProfilePage`'s existing `openChartHasNote` write-back
 * guard (`!!elationClinical?.openNote`) — deliberately the SAME predicate, so
 * "BP is showing" and "a note write-back can be attempted" can never disagree.
 * Note-id readability is deliberately NOT required: the id is unreadable on
 * some live templates whose hidden-input selectors don't match, and that is a
 * scrape limitation, not the absence of a note.
 *
 * ## Behaviour change, stated plainly
 *
 * `useElationBridge` returns disconnected defaults outside the extension, so
 * `clinical` is ALWAYS null in the standalone desktop web app. BP Recordings is
 * therefore now HIDDEN in standalone web, and appears ONLY in the CoPilot
 * side-panel sidecar with an EHR chart open and a visit note open in it.
 *
 * Passing `false` makes `@astrana/ui` hide the section AND skip the blood-
 * pressure fetch (`useBloodPressureHistory(patient, undefined, !showBloodPressure)`),
 * so this is a real data-fetch gate, not just a CSS one.
 *
 * @param clinical The live Elation bridge clinical payload; null outside the
 *   extension.
 */
export function isBpRecordingsVisible(
  clinical: ElationClinical | null | undefined,
): boolean {
  return !!clinical?.openNote;
}
